import { Component, type ErrorInfo, type ReactNode } from 'react';
import { reportLeak } from '@/lib/telemetry';
import { Button } from './ui/button';

/**
 * The last line of defence.
 *
 * A render crash on this surface must not put a stack trace on screen: the
 * component that crashed was probably rendering agent output, and a stack trace
 * is exactly the kind of technical text this product promises a client will
 * never see. So the boundary reports the failure and shows a plain sentence.
 */
export class ErrorBoundary extends Component<{ children: ReactNode }, { crashed: boolean }> {
  state = { crashed: false };

  static getDerivedStateFromError() {
    return { crashed: true };
  }

  componentDidCatch(error: Error, info: ErrorInfo) {
    reportLeak({
      kind: 'render_error',
      token: error.name || 'render',
      context: { component: info.componentStack?.split('\n')[1]?.trim().slice(0, 80) },
    });
    if (import.meta.env.DEV) {
      // eslint-disable-next-line no-console
      console.error(error, info);
    }
  }

  render() {
    if (!this.state.crashed) return this.props.children;

    return (
      <div className="chassis flex items-center justify-center px-[30px]">
        <div className="w-full max-w-[26rem] rounded-xl bg-bone-raised p-8">
          <h1 className="text-[1.375rem] font-bold tracking-[-0.025em] text-graphite-900">
            This page stopped responding
          </h1>
          <p className="mt-1.5 text-graphite-500">
            Nothing you asked for has been lost. Reload the page and it should come back.
          </p>
          <Button variant="primary" className="mt-5" onClick={() => window.location.reload()}>
            Reload
          </Button>
        </div>
      </div>
    );
  }
}
